import { useState, useEffect, useLayoutEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { content } from "./content";

const LangContext = createContext({ lang: "en", setLang: () => {} });

function useLang() {
  return useContext(LangContext);
}

const C = {
  bg:       "#F5F2EE",
  cream:    "#FAF8F5",
  sage:     "#3D5A4B",
  sageHov:  "#5C7A69",
  sagePale: "#EAF0EC",
  terra:    "#C4725A",
  surface:  "#EDE9E3",
  dark:     "#1C1917",
  muted:    "#7A7068",
  border:   "#DDD8D0",
  white:    "#FFFFFF",
};

const G = `
  @font-face {
    font-family: 'DM Sans';
    font-style: normal;
    font-weight: 300 500;
    font-display: swap;
    src: url('/fonts/dm-sans.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Playfair Display';
    font-style: italic;
    font-weight: 400;
    font-display: swap;
    src: url('/fonts/playfair-display-italic.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Playfair Display';
    font-style: normal;
    font-weight: 400 500;
    font-display: swap;
    src: url('/fonts/playfair-display.woff2') format('woff2');
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #F5F2EE; --cream: #FAF8F5; --sage: #3D5A4B;
    --sage-h: #5C7A69; --sage-p: #EAF0EC; --terra: #C4725A;
    --dark: #1C1917; --muted: #7A7068; --border: #DDD8D0;
  }

  html { scroll-behavior: smooth; font-size: 16px; }
  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg); color: var(--dark);
    overflow-x: hidden;
  }
  ::selection { background: rgba(61,90,75,0.15); }

  section { scroll-margin-top: 80px; }

  body::before {
    content: '';
    position: fixed; inset: 0; z-index: 9998; pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  .fu { opacity: 0; transform: translateY(28px); transition: opacity 680ms cubic-bezier(.16,1,.3,1), transform 680ms cubic-bezier(.16,1,.3,1); }
  .fu.in { opacity: 1; transform: none; }

  .stagger > * { opacity: 0; transform: translateY(20px); transition: opacity 600ms cubic-bezier(.16,1,.3,1), transform 600ms cubic-bezier(.16,1,.3,1); }
  .stagger > *.in { opacity: 1; transform: none; }

  @keyframes ticker {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @keyframes floatY {
    0%,100% { transform: translateY(0); }
    50%     { transform: translateY(-9px); }
  }
  @keyframes morphBlob {
    0%,100% { border-radius: 60% 40% 55% 45% / 50% 60% 40% 50%; }
    33%     { border-radius: 50% 60% 40% 55% / 45% 50% 60% 40%; }
    66%     { border-radius: 40% 55% 60% 45% / 60% 40% 55% 50%; }
  }
  @keyframes pulseRing {
    0%   { transform: scale(1); opacity: .5; }
    100% { transform: scale(1.9); opacity: 0; }
  }
  @keyframes slideDown {
    from { opacity:0; transform: translateY(-6px); }
    to   { opacity:1; transform: translateY(0); }
  }

  .nav-link { position: relative; text-decoration: none; }
  .nav-link::after {
    content: ''; position: absolute; bottom: -2px; left: 0;
    width: 100%; height: 1px; background: var(--sage);
    transform: scaleX(0); transform-origin: left;
    transition: transform 200ms ease;
  }
  .nav-link:hover::after { transform: scaleX(1); }
  .nav-link:hover { color: var(--sage) !important; }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; }
    .fu, .stagger > * { opacity:1; transform:none; }
  }
`;

function useFU(delay = 0) {
  const r = useRef(null);
  useEffect(() => {
    const el = r.current; if (!el) return;
    const t = setTimeout(() => {
      const obs = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { el.classList.add("in"); obs.unobserve(el); }
      }, { threshold: 0.1 });
      obs.observe(el);
      return () => obs.disconnect();
    }, delay);
    return () => clearTimeout(t);
  }, [delay]);
  return r;
}

function useStagger() {
  const r = useRef(null);
  useEffect(() => {
    const el = r.current; if (!el) return;
    const children = Array.from(el.children);
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        children.forEach((c, i) => {
          setTimeout(() => c.classList.add("in"), i * 130);
        });
        obs.unobserve(el);
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return r;
}

function ProgressBar() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setPct(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return <div style={{ position:"fixed", top:0, left:0, zIndex:9999, height:2, width:`${pct}%`, background:`linear-gradient(90deg,${C.terra},${C.sage})`, pointerEvents:"none", transition:"width 60ms linear" }} />;
}

const Label = ({ children, color = C.muted, style = {} }) => (
  <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:11, letterSpacing:"3px", textTransform:"uppercase", color, marginBottom:20, ...style }}>
    {children}
  </div>
);

const H2 = ({ children, color = C.dark, size = "clamp(26px,3.5vw,42px)", style = {} }) => (
  <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:400, fontSize:size, lineHeight:1.2, color, ...style }}>
    {children}
  </h2>
);

function Btn({ href, children, variant = "solid", style = {}, ...props }) {
  const base = {
    display:"inline-block", fontFamily:"'DM Sans',sans-serif", fontWeight:500,
    fontSize:14, letterSpacing:"0.5px", textDecoration:"none", cursor:"pointer",
    borderRadius:8, padding:"15px 36px", transition:"all 220ms ease", border:"none",
  };
  const solid = { background:C.sage, color:C.white, boxShadow:"0 3px 12px rgba(61,90,75,.18)" };
  const outline = { background:"transparent", color:C.sage, border:`1.5px solid ${C.sage}` };
  const ghost = { background:"transparent", color:C.dark, padding:"15px 0", borderBottom:`1px solid transparent` };

  const s = variant === "solid" ? solid : variant === "outline" ? outline : ghost;

  return (
    <a href={href} style={{ ...base, ...s, ...style }}
      onMouseEnter={e => {
        if (variant === "solid") { e.currentTarget.style.background = C.sageHov; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(61,90,75,.28)"; }
        if (variant === "outline") { e.currentTarget.style.background = C.sage; e.currentTarget.style.color = C.white; e.currentTarget.style.transform = "translateY(-2px)"; }
        if (variant === "ghost") { e.currentTarget.style.borderBottomColor = C.terra; e.currentTarget.style.color = C.terra; }
      }}
      onMouseLeave={e => {
        if (variant === "solid") { e.currentTarget.style.background = C.sage; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 3px 12px rgba(61,90,75,.18)"; }
        if (variant === "outline") { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.sage; e.currentTarget.style.transform = "translateY(0)"; }
        if (variant === "ghost") { e.currentTarget.style.borderBottomColor = "transparent"; e.currentTarget.style.color = C.dark; }
      }}
      {...props}>
      {children}
    </a>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Lock body scroll while the mobile menu is open — on iOS Safari, a
  // position:fixed overlay can render anchored to a stale viewport if the
  // page is scrolled and the address bar collapses, making it invisible.
  // Freezing the page removes that interaction entirely. useLayoutEffect
  // (not useEffect) so this applies before the browser paints the open
  // state — otherwise there's a visible flash of the unlocked frame.
  useLayoutEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const { lang, setLang } = useLang();
  const t = content[lang].nav;
  const links = t.links;
  // Section anchors (#about, #contact, etc.) only exist on the homepage —
  // on the legal pages, prefix with "/" so the browser navigates back to
  // the homepage first instead of silently doing nothing.
  const { pathname } = useLocation();
  const sectionHref = (hash) => (pathname === "/" ? hash : `/${hash}`);

  return (
    <nav style={{
      position:"fixed", top:2, left:0, right:0, zIndex:1000,
      height: scrolled ? 60 : 72,
      background: scrolled ? "rgba(250,248,245,.96)" : "transparent",
      backdropFilter: scrolled ? "blur(18px)" : "none",
      borderBottom: scrolled ? `1px solid ${C.border}` : "none",
      transition:"all 300ms ease",
      display:"flex", alignItems:"center", padding:"0 clamp(20px,4vw,56px)",
    }}>
      <a href={sectionHref("#home")} style={{ display:"flex", alignItems:"center", textDecoration:"none", flexShrink:0, marginRight:"auto" }}>
        <picture>
          <source media="(max-width: 820px)" srcSet="/logo-compact.png" />
          <img src="/logo.png" alt="Clarity Counselling Victoria" className="logo-nav" style={{ width:"auto" }} />
        </picture>
      </a>

      <div style={{ display:"flex", gap:36, alignItems:"center" }} className="desk-nav">
        {links.map(l => (
          <a key={l.label} href={sectionHref(l.href)} className="nav-link"
            style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, letterSpacing:"0.5px", color:C.dark, textDecoration:"none", whiteSpace:"nowrap" }}>
            {l.label}
          </a>
        ))}
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {["en", "fr"].map((code) => (
            <button key={code} onClick={() => setLang(code)} style={{
              background: "none", border: "none", cursor: "pointer", padding: "4px 6px",
              fontFamily: "'DM Sans',sans-serif", fontWeight: lang === code ? 600 : 400, fontSize: "12px",
              letterSpacing: "0.5px", color: lang === code ? C.sage : C.muted,
            }}>{code.toUpperCase()}</button>
          ))}
        </div>
        <Btn href="#contact" style={{ padding:"10px 24px", borderRadius:7, fontSize:13 }}>{t.contactBtn}</Btn>
      </div>

      <button onClick={() => setOpen(o => !o)} className="hamburger"
        style={{ display:"none", background:"none", border:"none", cursor:"pointer", padding:8, marginLeft:12 }}
        aria-label="Toggle menu">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.dark} strokeWidth="1.5">
          {open
            ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
            : <><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></>}
        </svg>
      </button>

      {open && (
        <div style={{
          position:"fixed", top:0, left:0, right:0, background:C.sage,
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
          gap:32, zIndex:999, animation:"slideDown .3s ease both",
        }} className="mobile-menu-overlay">
          <a href={sectionHref("#home")} onClick={() => setOpen(false)} style={{ display:"flex", alignItems:"center", textDecoration:"none", marginBottom:16 }}>
            <img src="/logo.png" alt="Clarity Counselling Victoria" style={{ width:"min(300px,70vw)", height:"auto", filter:"brightness(0) invert(1)" }} />
          </a>
          {links.map(l => (
            <a key={l.label} href={sectionHref(l.href)} onClick={() => setOpen(false)}
              style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:26, color:"rgba(255,255,255,.85)", textDecoration:"none", letterSpacing:"1px" }}>
              {l.label}
            </a>
          ))}
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            {["en", "fr"].map((code) => (
              <button key={code} onClick={() => setLang(code)} style={{
                background: "none", border: "none", cursor: "pointer", padding: "8px 14px",
                fontFamily: "'DM Sans',sans-serif", fontWeight: lang === code ? 600 : 400, fontSize: "18px",
                letterSpacing: "0.5px", color: lang === code ? "white" : "rgba(255,255,255,.6)",
              }}>{code.toUpperCase()}</button>
            ))}
          </div>
          <Btn href="#contact" style={{ marginTop:16, padding:"16px 48px" }} onClick={() => setOpen(false)}>{t.contactBtn}</Btn>
          <button onClick={() => setOpen(false)} style={{ position:"absolute", top:24, right:24, background:"none", border:"none", cursor:"pointer" }}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.5">
              <line x1="22" y1="6" x2="6" y2="22"/><line x1="6" y1="6" x2="22" y2="22"/>
            </svg>
          </button>
        </div>
      )}

      <style>{`
        .logo-nav { height: 44px; }
        .mobile-menu-overlay { height: 100vh; height: 100dvh; }
        @media (max-width: 820px) {
          .desk-nav { display: none !important; }
          .hamburger { display: flex !important; }
          .logo-nav { height: 52px; }
        }
      `}</style>
    </nav>
  );
}

function Hero() {
  const { lang } = useLang();
  const t = content[lang].hero;
  const words1 = t.titleWords;

  return (
    <section id="home" style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column", justifyContent:"center", padding:"120px clamp(20px,4vw,56px) 60px", position:"relative", overflow:"hidden" }}>

      <div style={{ position:"absolute", top:"10%", right:"6%", width:300, height:300, borderRadius:"50%", border:`1.5px solid rgba(196,114,90,.18)`, pointerEvents:"none" }} />
      <div style={{ position:"absolute", bottom:"12%", left:"3%", width:150, height:150, borderRadius:"50%", border:`1.5px solid rgba(61,90,75,.14)`, pointerEvents:"none" }} />
      <div style={{ position:"absolute", top:0, right:"42%", width:1, height:"100%", background:`linear-gradient(to bottom, transparent, rgba(61,90,75,.1) 30%, rgba(61,90,75,.1) 70%, transparent)`, pointerEvents:"none" }} className="desk-only" />

      <div style={{ maxWidth:1160, margin:"0 auto", width:"100%", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"clamp(32px,5vw,80px)", alignItems:"center" }} className="hero-grid">

        <div>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:11, letterSpacing:"3.5px", textTransform:"uppercase", color:C.terra, marginBottom:32, animation:"slideDown .5s .1s ease both" }}>
            &#10022; {t.kicker}
          </div>

          <h1 style={{ fontFamily:"'Playfair Display',serif", fontWeight:300, fontSize:"clamp(38px,5.5vw,68px)", lineHeight:1.12, color:C.dark, marginBottom:8 }}>
            {words1.map((w, i) => (
              <span key={i} style={{ display:"inline-block", opacity:0, transform:"translateY(20px)",
                animation:`slideDown .55s ${.3 + i*.1}s cubic-bezier(.16,1,.3,1) both` }}>
                {w}{" "}
              </span>
            ))}
          </h1>

          <div style={{ width:80, height:2, background:`linear-gradient(90deg,${C.terra},rgba(196,114,90,0))`, borderRadius:2, margin:"32px 0" }} />

          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:18, lineHeight:1.85, color:C.muted, maxWidth:440, marginBottom:36, opacity:0, animation:"slideDown .6s .9s ease both" }}>
            {t.subtitle}
          </p>

          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:40, opacity:0, animation:"slideDown .5s 1.05s ease both" }}>
            {t.badges.map(b => (
              <span key={b} style={{ background:C.sagePale, borderRadius:20, padding:"8px 16px", fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:12, color:C.sage }}>
                {b}
              </span>
            ))}
          </div>

          <div style={{ display:"flex", gap:20, flexWrap:"wrap", opacity:0, animation:"slideDown .5s 1.15s ease both" }}>
            <Btn href="#contact">{t.ctaPrimary}</Btn>
            <Btn href="#about" variant="ghost">{t.ctaSecondary}</Btn>
          </div>
        </div>

        <div style={{ position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }} className="hero-photo-col">

          <div style={{
            width:"min(380px,80vw)", aspectRatio:"0.85",
            animation:"morphBlob 20s ease-in-out infinite",
            position:"relative", overflow:"hidden",
          }}>
            <img src={t.heroImage} alt="Sarah Cotillon en séance de counselling" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"50% 20%" }} />
          </div>

          <div className="rating-badge" style={{
            position:"absolute", bottom:"8%", left:"-8%",
            background:"rgba(250,248,245,.86)", backdropFilter:"blur(18px) saturate(1.4)",
            border:"1px solid rgba(255,255,255,.72)", borderRadius:16,
            padding:"20px 26px", animation:"floatY 5s ease-in-out infinite",
            boxShadow:"0 8px 32px rgba(28,25,23,.09)", minWidth:210, zIndex:10,
          }}>
            <div style={{ fontSize:15, marginBottom:6 }}>&#11088;&#11088;&#11088;&#11088;&#11088;</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:13, color:C.dark, marginBottom:2 }}>{t.trustedTitle}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:12, color:C.muted }}>{t.trustedSub}</div>
          </div>

          <div style={{
            position:"absolute", top:"-6%", right:"-8%", maxWidth:180,
            background:"rgba(61,90,75,.92)", backdropFilter:"blur(12px)",
            borderRadius:12, padding:"12px 16px",
            boxShadow:"0 6px 24px rgba(28,25,23,.16)",
          }} className="desk-only">
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:11, letterSpacing:"1.5px", color:"rgba(255,255,255,.5)", textTransform:"uppercase", marginBottom:4 }}>{t.credentialsLabel}</div>
            {t.credentials.map((cr, i) => (
              <div key={i} style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:12, color:"rgba(255,255,255,.8)", lineHeight:1.6 }}>&#10003; {cr}</div>
            ))}
          </div>

          <div className="mob-only" style={{ display:"none", flexWrap:"wrap", gap:8, justifyContent:"center", marginTop:24 }}>
            {t.credentials.map((cr, i) => (
              <span key={i} style={{ background:C.sagePale, borderRadius:20, padding:"8px 14px", fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:12, color:C.sage }}>
                &#10003; {cr}
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 820px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .desk-only { display: none !important; }
          .hero-photo-col { flex-direction: column !important; }
          .mob-only { display: flex !important; }
          .rating-badge { left: -2% !important; bottom: auto !important; top: calc(min(380px,80vw) / 0.85 - 92px) !important; }
        }
      `}</style>
    </section>
  );
}

function Ticker() {
  const { lang } = useLang();
  const items = content[lang].ticker;
  const all = [...items, ...items];
  return (
    <div style={{ background:C.sage, height:52, overflow:"hidden", display:"flex", alignItems:"center" }}>
      <div style={{ display:"flex", animation:"ticker 35s linear infinite", whiteSpace:"nowrap", willChange:"transform" }}>
        {all.map((item, i) => (
          <span key={i} style={{
            padding:"0 28px",
            fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:11,
            letterSpacing:"3px", textTransform:"uppercase",
            color:"rgba(255,255,255,.65)",
          }}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function Help() {
  const titleRef = useFU();
  const cardsRef = useStagger();
  const { lang } = useLang();
  const t = content[lang].howIHelp;

  return (
    <section id="help" style={{ background:C.cream, padding:"100px clamp(20px,4vw,56px)" }}>
      <div style={{ maxWidth:1000, margin:"0 auto", textAlign:"center" }}>
        <div ref={titleRef} className="fu">
          <Label>{t.kicker}</Label>
          <H2 style={{ marginBottom:20 }}>{t.titleLine1}<br />{t.titleLine2}</H2>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:17, lineHeight:1.85, color:C.muted, marginBottom:52 }}>
            {t.intro}
          </p>
        </div>

        <div ref={cardsRef} className="focus-grid stagger" style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:24, textAlign:"left", marginBottom:36 }}>
          {t.focusAreas.map((area, i) => (
            <div key={i} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:16, padding:"32px 28px", boxShadow:"0 4px 20px rgba(28,25,23,.05)" }}>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontWeight:500, fontSize:19, color:C.sage, marginBottom:12 }}>{area.title}</h3>
              <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:14, lineHeight:1.7, color:C.muted }}>{area.body}</p>
            </div>
          ))}
        </div>

        <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontStyle:"italic", fontSize:15, color:C.muted }}>
          {t.footnote}
        </p>
      </div>
      <style>{`@media (max-width: 768px) { .focus-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function About() {
  const ref = useFU();
  const { lang } = useLang();
  const t = content[lang].about;

  return (
    <section id="about" style={{ background:C.bg, padding:"100px clamp(20px,4vw,56px)" }}>
      <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"45% 55%", gap:"clamp(32px,5vw,80px)", alignItems:"center" }} className="two-col">

        <div style={{ position:"relative" }}>
          <div style={{
            width:"100%", aspectRatio:"4/5",
            borderRadius:"58% 42% 52% 48% / 48% 52% 48% 52%",
            position:"relative", overflow:"hidden",
          }}>
            <img src={t.aboutImage} alt="Sarah Cotillon" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} />
          </div>
          <div style={{
            position:"absolute", bottom:20, right:-16,
            background:"rgba(250,248,245,.9)", backdropFilter:"blur(16px)",
            border:"1px solid rgba(255,255,255,.7)", borderRadius:12,
            padding:"16px 20px", boxShadow:"0 6px 24px rgba(28,25,23,.1)",
            maxWidth:190,
          }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:11, color:C.sage, letterSpacing:"0.5px", marginBottom:4 }}>{t.credentialLine1}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:10, color:C.muted }}>{t.credentialLine2}</div>
          </div>
        </div>

        <div ref={ref} className="fu">
          <Label color={C.terra}>{t.kicker}</Label>
          <H2 style={{ marginBottom:24 }}>{t.titleLine1}<br />{t.titleLine2}</H2>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:16, lineHeight:1.9, color:C.dark, marginBottom:24 }}>
            {t.p1}
          </p>
          <div style={{ width:60, height:2, background:C.sage, margin:"24px 0" }} />
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:16, lineHeight:1.9, color:C.dark, marginBottom:16 }}>
            {t.p2}
          </p>
          <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:8, marginBottom:36 }}>
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, color:C.muted }}>{t.affiliationsLabel}</span>
            {t.affiliations.map((a, i) => (
              <span key={a.url} style={{ display:"inline-flex", alignItems:"center" }}>
                <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, color:C.sage, textDecoration:"underline" }}>{a.label}</a>
                {i < t.affiliations.length - 1 && <span style={{ color:C.muted, marginLeft:8 }}>·</span>}
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`@media (max-width:820px) { .two-col { grid-template-columns:1fr !important; } }`}</style>
    </section>
  );
}

function ApproachCard({ card }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.07)",
        border:"1px solid rgba(255,255,255,.13)", borderRadius:16,
        padding:"36px 28px", transition:"all 300ms ease",
        transform: hov ? "translateY(-4px)" : "none",
      }}>
      <div style={{ marginBottom:20 }}>{card.icon}</div>
      <h3 style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:18, color:C.white, marginBottom:12 }}>{card.title}</h3>
      <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:15, color:"rgba(255,255,255,.72)", lineHeight:1.75, marginBottom:20 }}>{card.body}</p>
      <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:11, color:"rgba(255,255,255,.4)", letterSpacing:"0.5px" }}>{card.tag}</span>
    </div>
  );
}

function Approach() {
  const ref = useFU();
  const staggerRef = useStagger();
  const { lang } = useLang();
  const t = content[lang].approach;

  const icons = [
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="rgba(255,255,255,.72)" strokeWidth="1.5"><ellipse cx="18" cy="18" rx="13" ry="8"/><circle cx="18" cy="18" r="3.5" fill="rgba(255,255,255,.4)" stroke="none"/><path d="M5 18 Q10 8 18 18 Q26 28 31 18"/></svg>,
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="rgba(255,255,255,.72)" strokeWidth="1.5"><circle cx="13" cy="14" r="7"/><circle cx="23" cy="14" r="7"/><path d="M10 21 Q14 29 18 27 Q22 29 26 21"/></svg>,
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="rgba(255,255,255,.72)" strokeWidth="1.5"><path d="M18 4 L18 24"/><path d="M11 16 Q7 22 9 29 Q12 34 18 32"/><path d="M25 16 Q29 22 27 29 Q24 34 18 32"/><path d="M13 10 Q8 14 8 20"/><path d="M23 10 Q28 14 28 20"/></svg>,
  ];
  const cards = t.cards.map((card, i) => ({ ...card, icon: icons[i] }));

  return (
    <section id="approach" style={{ background:C.sage, padding:"100px clamp(20px,4vw,56px)" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <div ref={ref} className="fu" style={{ textAlign:"center", marginBottom:60 }}>
          <Label color="rgba(196,114,90,.85)">{t.kicker}</Label>
          <H2 color={C.white} size="clamp(26px,3.5vw,42px)">
            {t.titleLine1}<br />{t.titleLine2}
          </H2>
        </div>

        <div ref={staggerRef} className="stagger" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:24 }} id="approach-grid">
          {cards.map((card, i) => <ApproachCard key={i} card={card} />)}
        </div>

        <p style={{ textAlign:"center", fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:14, color:"rgba(255,255,255,.5)", marginTop:40, maxWidth:660, margin:"40px auto 0" }}>
          {t.footnote}
        </p>
      </div>
      <style>{`@media (max-width:820px) { #approach-grid { grid-template-columns:1fr !important; } }`}</style>
    </section>
  );
}

function Testimonials() {
  const [active, setActive] = useState(0);
  const ref = useFU();
  const { lang } = useLang();
  const t = content[lang].testimonials;
  const reviews = t.items;

  return (
    <section style={{ background:C.cream, padding:"100px clamp(20px,4vw,56px)" }}>
      <div style={{ maxWidth:860, margin:"0 auto" }}>
        <div ref={ref} className="fu" style={{ textAlign:"center", marginBottom:56 }}>
          <Label>{t.kicker}</Label>
          <H2>{t.titleLine1}<br />{t.titleLine2}</H2>
        </div>

        <div style={{
          background:C.white, borderRadius:16, padding:"clamp(28px,4vw,52px) clamp(24px,4vw,52px)",
          border:`1px solid ${C.border}`, boxShadow:"0 4px 28px rgba(28,25,23,.07)",
          position:"relative", overflow:"hidden", minHeight:220,
          transition:"box-shadow 300ms ease",
        }}>
          <div style={{ position:"absolute", top:-12, left:24, fontFamily:"'Playfair Display',serif", fontWeight:300, fontSize:130, color:"rgba(61,90,75,.1)", lineHeight:1, pointerEvents:"none", userSelect:"none" }}>"</div>
          <p style={{ fontFamily:"'Playfair Display',serif", fontStyle:"italic", fontWeight:300, fontSize:"clamp(16px,2vw,20px)", lineHeight:1.72, color:C.dark, marginBottom:28, position:"relative" }}>
            {reviews[active].q}
          </p>
          <div>
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:13, color:C.sage }}>&mdash; {reviews[active].who}</span>
            <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:12, color:C.muted, marginLeft:12 }}>{reviews[active].type}</span>
          </div>
        </div>

        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:14, marginTop:32 }}>
          {["←","→"].map((arrow, idx) => (
            <button key={arrow} onClick={() => setActive(a => idx === 0 ? (a - 1 + reviews.length) % reviews.length : (a + 1) % reviews.length)}
              style={{ width:40, height:40, borderRadius:"50%", border:`1px solid ${C.sage}`, background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color:C.sage, transition:"all 200ms ease" }}
              onMouseEnter={e => { e.currentTarget.style.background=C.sage; e.currentTarget.style.color=C.white; }}
              onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color=C.sage; }}>
              {arrow}
            </button>
          ))}
          {reviews.map((_, i) => (
            <button key={i} onClick={() => setActive(i)} style={{
              width: i === active ? 24 : 8, height:8, borderRadius:4, border:"none", cursor:"pointer",
              background: i === active ? C.sage : C.border, transition:"all 300ms ease",
            }} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TelehealthMiniCard({ card }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:C.white, border:`1px solid ${hov ? "rgba(61,90,75,.35)" : "rgba(61,90,75,.18)"}`,
        borderRadius:12, padding:"24px 28px",
        transition:"all 220ms ease",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? "0 8px 24px rgba(61,90,75,.1)" : "none",
        display:"flex", gap:16, alignItems:"flex-start",
      }}>
      <span style={{ fontSize:22 }}>{card.icon}</span>
      <div>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:15, color:C.dark, marginBottom:5 }}>{card.title}</div>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:13, color:C.muted, lineHeight:1.6 }}>{card.sub}</div>
      </div>
    </div>
  );
}

function Telehealth() {
  const ref = useFU();
  const { lang } = useLang();
  const t = content[lang].telehealth;
  const miniCards = t.miniCards;

  return (
    <section id="telehealth" style={{ background:C.bg, padding:"100px clamp(20px,4vw,56px)" }}>
      <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"55% 45%", gap:"clamp(32px,5vw,72px)", alignItems:"start" }} className="two-col">

        <div ref={ref} className="fu">
          <Label>{t.kicker}</Label>
          <H2 style={{ marginBottom:24 }}>{t.title}</H2>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:17, lineHeight:1.85, color:C.muted, marginBottom:36 }}>
            {t.intro}
          </p>

          <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:16, marginBottom:44 }}>
            {t.list.map(item => (
              <li key={item} style={{ display:"flex", gap:12, alignItems:"flex-start", fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:16, color:C.dark, lineHeight:1.6 }}>
                <span style={{ color:C.sage, fontSize:16, marginTop:1, flexShrink:0 }}>&#10003;</span>
                {item}
              </li>
            ))}
          </ul>

          <Btn href="#contact">{t.cta}</Btn>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {miniCards.map(card => <TelehealthMiniCard key={card.title} card={card} />)}
        </div>
      </div>
    </section>
  );
}

function Quote() {
  const ref = useFU();
  const { lang } = useLang();
  const t = content[lang].quote;
  return (
    <section style={{ background:C.sage, padding:"100px clamp(20px,4vw,56px)", textAlign:"center", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:-80, left:-80, width:320, height:320, borderRadius:"50%", background:"rgba(92,122,105,.15)", pointerEvents:"none" }} />
      <div style={{ position:"absolute", bottom:-60, right:-60, width:240, height:240, borderRadius:"50%", background:"rgba(92,122,105,.1)", pointerEvents:"none" }} />
      <div style={{ position:"absolute", top:20, left:40, fontFamily:"'Playfair Display',serif", fontWeight:300, fontSize:140, color:"rgba(255,255,255,.07)", lineHeight:1, pointerEvents:"none", userSelect:"none" }}>"</div>

      <div ref={ref} className="fu" style={{ maxWidth:680, margin:"0 auto", position:"relative" }}>
        <p style={{ fontFamily:"'Playfair Display',serif", fontStyle:"italic", fontWeight:300, fontSize:"clamp(20px,2.5vw,26px)", color:C.white, lineHeight:1.65, marginBottom:32 }}>
          "{t.text}"
        </p>
        <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:14, color:"rgba(196,114,90,.85)", letterSpacing:"1px" }}>
          &mdash; Sarah Cotillon &nbsp;&middot;&nbsp; Clarity Counselling Victoria
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const ref = useFU();
  const { lang } = useLang();
  const t = content[lang].cta;
  return (
    <section style={{ background:C.cream, padding:"120px clamp(20px,4vw,56px)", textAlign:"center", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:500, height:500, borderRadius:"60% 40% 55% 45%/50% 60% 40% 55%", background:C.sagePale, opacity:.5, pointerEvents:"none", zIndex:0 }} />

      <div ref={ref} className="fu" style={{ position:"relative", zIndex:1 }}>
        <Label color={C.terra} style={{ marginBottom:20 }}>{t.kicker}</Label>
        <H2 size="clamp(30px,4.5vw,52px)" style={{ marginBottom:24 }}>{t.titleLine1}<br />{t.titleLine2}</H2>
        <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:18, lineHeight:1.85, color:C.muted, maxWidth:560, margin:"0 auto 48px" }}>
          {t.body}
        </p>

        <div style={{ display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap", marginBottom:44 }}>
          <Btn href="#contact" style={{ padding:"18px 48px", borderRadius:10, fontSize:15 }}>{t.primaryBtn}</Btn>
          <Btn href="tel:+61488988234" variant="outline" style={{ padding:"18px 36px", borderRadius:10, fontSize:15 }}>{t.phoneBtn}</Btn>
        </div>

        <div style={{ display:"flex", gap:"clamp(16px,3vw,40px)", justifyContent:"center", flexWrap:"wrap" }}>
          {t.badges.map(b => (
            <span key={b} style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:12, color:C.sage }}>{b}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xbdnlkgb";
const TURNSTILE_SITEKEY = "0x4AAAAAAEeNQLDmh6VEQbVw";

function Turnstile({ onToken }) {
  const ref = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile || !ref.current) { setTimeout(tryRender, 100); return; }
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_SITEKEY,
        callback: (token) => onToken(token),
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
      });
    };
    tryRender();
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current != null) window.turnstile.remove(widgetId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} />;
}

// status: "idle" | "sending" | "sent" | "error"
function useContactForm(uiLang) {
  const [form, setForm] = useState({ name:"", email:"", phone:"", topicIndex:-1, message:"", lang:"English" });
  const [status, setStatus] = useState("idle");
  const [errorDetail, setErrorDetail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (e.target._gotcha.value) { setStatus("sent"); return; } // honeypot tripped: fake success, never reveal the block to bot or human
    if (!turnstileToken) {
      setErrorDetail(content[uiLang].contactForm.turnstileMissing);
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const topic = form.topicIndex >= 0 ? content[uiLang].contactForm.topicOptions[form.topicIndex] : "";
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          topic, message: form.message, language: form.lang,
          "cf-turnstile-response": turnstileToken,
          _subject: "New enquiry from claritycounsellingvic.com",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.errors?.map(e => e.message).join(", ") || "Formspree submission failed");
      }
      setStatus("sent");
    } catch (err) {
      console.error("Contact form submission failed:", err);
      setErrorDetail(err.message || "");
      setStatus("error");
      setTurnstileToken(""); // single-use token — caller remounts the widget for a fresh one
    }
  };

  return { form, setForm, status, errorDetail, handleSubmit, setTurnstileToken };
}

function MapEmbed() {
  const { lang } = useLang();
  const t = content[lang].contactForm;
  const [loaded, setLoaded] = useState(() => {
    try { return window.localStorage.getItem("clarity-map-consent") === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    const onConsentChanged = () => {
      try { if (window.localStorage.getItem("clarity-map-consent") === "1") setLoaded(true); }
      catch {}
    };
    window.addEventListener("clarity-consent-changed", onConsentChanged);
    return () => window.removeEventListener("clarity-consent-changed", onConsentChanged);
  }, []);

  const load = () => {
    setLoaded(true);
    try { window.localStorage.setItem("clarity-map-consent", "1"); } catch {}
  };

  if (!loaded) {
    return (
      <div style={{ width:"100%", height:320, borderRadius:16, marginBottom:32, background:C.surface, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14, border:`1px solid ${C.border}` }}>
        <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:13, color:C.muted, textAlign:"center", maxWidth:280, margin:0 }}>
          {t.mapPrivacyNote}
        </p>
        <button type="button" onClick={load}
          style={{ padding:"10px 24px", background:C.sage, color:C.white, border:"none", borderRadius:20, fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:13, cursor:"pointer" }}>
          {t.mapLoadLabel}
        </button>
      </div>
    );
  }

  return (
    <iframe
      title="Clarity Counselling Victoria location"
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3150.9719597128433!2d144.9658534!3d-37.83754229999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad6694fce93c991%3A0xb4f04c8aa9416bc5!2sClarity%20Counselling%20Victoria!5e0!3m2!1sen!2sfr!4v1789459140621!5m2!1sen!2sfr"
      width="100%" height="320"
      style={{ border:0, borderRadius:16, marginBottom:32, display:"block" }}
      allowFullScreen
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
}

function Contact() {
  const { lang } = useLang();
  const { form, setForm, status, errorDetail, handleSubmit, setTurnstileToken } = useContactForm(lang);
  const ref = useFU();
  const t = content[lang].contactForm;
  const [turnstileKey, setTurnstileKey] = useState(0);

  useEffect(() => {
    if (status === "error") setTurnstileKey(k => k + 1); // remount widget for a fresh single-use token
  }, [status]);

  const requiredMark = (right = 16) => (
    <span aria-hidden="true" style={{ position:"absolute", top:14, right, color:C.terra, fontSize:15, lineHeight:1, pointerEvents:"none" }}>*</span>
  );

  const field = (key, label, type = "text", required = true) => (
    <div style={{ position:"relative" }}>
      <input type={type} placeholder={label} required={required} value={form[key]}
        onChange={e => { e.target.setCustomValidity(""); setForm(f => ({ ...f, [key]: e.target.value })); }}
        onInvalid={e => e.target.setCustomValidity(e.target.validity.typeMismatch ? t.fieldInvalidEmail : t.fieldRequired)}
        style={{ width:"100%", padding:"15px 18px", border:`1.5px solid ${C.border}`, borderRadius:10, fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:15, color:C.dark, background:C.white, outline:"none", transition:"border 200ms, box-shadow 200ms" }}
        onFocus={e => { e.target.style.border=`1.5px solid ${C.sage}`; e.target.style.boxShadow=`0 0 0 4px rgba(61,90,75,.1)`; }}
        onBlur={e => { e.target.style.border=`1.5px solid ${C.border}`; e.target.style.boxShadow="none"; }}
      />
      {required && requiredMark()}
    </div>
  );

  return (
    <section id="contact" style={{ background:C.bg, padding:"100px clamp(20px,4vw,56px)" }}>
      <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"55% 45%", gap:"clamp(32px,5vw,72px)" }} className="two-col">

        <div ref={ref} className="fu">
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:400, fontSize:"clamp(26px,3vw,38px)", color:C.dark, marginBottom:12 }}>{t.title}</h2>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:16, color:C.muted, marginBottom:24 }}>
            {t.intro}
          </p>

          <div style={{ display:"flex", gap:12, alignItems:"flex-start", background:"rgba(196,114,90,.08)", border:`1px solid rgba(196,114,90,.25)`, borderRadius:12, padding:"16px 18px", marginBottom:36 }}>
            <span aria-hidden="true" style={{ fontSize:18, lineHeight:1, flexShrink:0 }}>💚</span>
            <p style={{ margin:0, fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, lineHeight:1.6, color:C.dark }}>
              {t.crisisNote}
            </p>
          </div>

          {status === "sent" ? (
            <div style={{ background:C.sagePale, border:`1px solid rgba(61,90,75,.3)`, borderRadius:14, padding:"36px 32px", textAlign:"center" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>&#10003;</div>
              <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:16, color:C.sage }}>{t.successMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:18 }}>
              <input type="text" name="_gotcha" tabIndex="-1" autoComplete="off" style={{ position:"absolute", left:"-9999px" }} aria-hidden="true" />
              {field("name",t.nameLabel)}
              {field("email",t.emailLabel,"email")}
              {field("phone",t.phoneLabel,"tel",false)}

              <div style={{ position:"relative" }}>
                <select value={form.topicIndex === -1 ? "" : form.topicIndex} required
                  onChange={e => { e.target.setCustomValidity(""); setForm(f => ({ ...f, topicIndex: e.target.value === "" ? -1 : Number(e.target.value) })); }}
                  onInvalid={e => e.target.setCustomValidity(t.fieldRequired)}
                  style={{ width:"100%", padding:"15px 18px", border:`1.5px solid ${C.border}`, borderRadius:10, fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:15, color: form.topicIndex !== -1 ? C.dark : C.muted, background:C.white, outline:"none" }}>
                  <option value="" disabled>{t.topicPlaceholder}</option>
                  {t.topicOptions.map((o, i) => <option key={i} value={i}>{o}</option>)}
                </select>
                {requiredMark(38)}
              </div>

              <div style={{ position:"relative" }}>
                <textarea rows={4} placeholder={t.messagePlaceholder} value={form.message} required
                  onChange={e => { e.target.setCustomValidity(""); setForm(f => ({ ...f, message:e.target.value })); }}
                  onInvalid={e => e.target.setCustomValidity(t.fieldRequired)}
                  style={{ width:"100%", padding:"15px 18px", border:`1.5px solid ${C.border}`, borderRadius:10, fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:15, color:C.dark, background:C.white, outline:"none", resize:"vertical", transition:"border 200ms, box-shadow 200ms" }}
                  onFocus={e => { e.target.style.border=`1.5px solid ${C.sage}`; e.target.style.boxShadow=`0 0 0 4px rgba(61,90,75,.1)`; }}
                  onBlur={e => { e.target.style.border=`1.5px solid ${C.border}`; e.target.style.boxShadow="none"; }}
                />
                {requiredMark()}
              </div>

              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, color:C.muted }}>{t.preferredLanguageLabel}</span>
                {t.langOptions.map(opt => (
                  <button key={opt.value} type="button" onClick={() => setForm(f => ({ ...f, lang:opt.value }))}
                    style={{ padding:"6px 18px", borderRadius:20, border:`1px solid ${form.lang===opt.value ? C.sage : C.border}`, background: form.lang===opt.value ? C.sagePale : "transparent", color: form.lang===opt.value ? C.sage : C.muted, fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, cursor:"pointer", transition:"all 200ms" }}>
                    {opt.label}
                  </button>
                ))}
              </div>

              <Turnstile key={turnstileKey} onToken={setTurnstileToken} />

              {status === "error" && (
                <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, color:C.terra }}>
                  {errorDetail && errorDetail !== "Formspree submission failed"
                    ? `${t.checkErrorPrefix} ${errorDetail}`
                    : t.genericError}
                </p>
              )}

              <button type="submit" disabled={status === "sending"}
                style={{ width:"100%", padding:18, background:C.sage, color:C.white, border:"none", borderRadius:10, fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:15, cursor: status === "sending" ? "default" : "pointer", opacity: status === "sending" ? 0.7 : 1, transition:"background 200ms, transform 200ms" }}
                onMouseEnter={e => { if (status !== "sending") { e.currentTarget.style.background=C.sageHov; e.currentTarget.style.transform="translateY(-1px)"; } }}
                onMouseLeave={e => { e.currentTarget.style.background=C.sage; e.currentTarget.style.transform="none"; }}>
                {status === "sending" ? t.sendingBtn : t.sendBtn}
              </button>

              <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:12, color:C.muted, textAlign:"center" }}>
                {t.secureNote}
              </p>
            </form>
          )}
        </div>

        <div style={{ paddingTop:8 }}>
          <MapEmbed />

          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            {[
              { icon:"📍", text:t.infoAddress, sub:t.infoAddressSub },
              { icon:"📞", text:t.infoPhone },
              { icon:"🕐", text:t.infoHours, sub:t.infoHoursSub },
            ].map(info => (
              <div key={info.text} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
                <span style={{ fontSize:18 }}>{info.icon}</span>
                <div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:14, color:C.dark }}>{info.text}</div>
                  {info.sub && <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:12, color:C.muted, marginTop:2 }}>{info.sub}</div>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop:24, padding:"14px 18px", background:C.sagePale, borderRadius:10, border:`1px solid rgba(61,90,75,.2)` }}>
            <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, color:C.sage }}>
              {t.telehealthNote}
            </p>
          </div>

          <p style={{ marginTop:14, fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:12, color:C.muted }}>{t.parkingNote}</p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { lang } = useLang();
  const t = content[lang].footer;
  const cols = [
    {
      head: null,
      content: (
        <div>
          <div style={{ marginBottom:16 }}>
            <img src="/logo-headway.jpg" alt="Clarity Counselling Victoria" style={{ height:72, width:"auto", borderRadius:"50%" }} />
          </div>
          <p style={{ fontFamily:"'Playfair Display',serif", fontStyle:"italic", fontWeight:300, fontSize:13, color:"rgba(255,255,255,.32)", marginBottom:24, lineHeight:1.6 }}>
            {t.tagline}
          </p>
        </div>
      ),
    },
    {
      head:t.navigateHeading,
      content: (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {t.navLinks.map(([l,h]) => (
            <a key={l} href={h} style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:14, color:"rgba(255,255,255,.48)", textDecoration:"none", transition:"color 200ms" }}
              onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,.88)"}
              onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,.48)"}>
              {l}
            </a>
          ))}
        </div>
      ),
    },
    {
      head:t.contactHeading,
      content: (
        <div>
          {t.contactLines.map(line => (
            <p key={line} style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:13, color:"rgba(255,255,255,.45)", marginBottom:8, lineHeight:1.6 }}>{line}</p>
          ))}
          <div style={{ marginTop:20, display:"flex", gap:8, flexWrap:"wrap" }}>
            {t.badges.map(b => (
              <span key={b} style={{ padding:"4px 12px", border:"1px solid rgba(255,255,255,.2)", borderRadius:20, fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:10, color:"rgba(255,255,255,.4)", letterSpacing:"0.5px" }}>{b}</span>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <footer style={{ background:C.dark, padding:"72px clamp(20px,4vw,56px) 36px" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:48, marginBottom:52 }} className="footer-grid">
          {cols.map((col, i) => (
            <div key={i}>
              {col.head && (
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:11, letterSpacing:"3px", textTransform:"uppercase", color:C.sagePale, marginBottom:20 }}>
                  {col.head}
                </div>
              )}
              {col.content}
            </div>
          ))}
        </div>
        <div style={{ borderTop:"1px solid rgba(255,255,255,.09)", paddingTop:28, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:11, color:"rgba(255,255,255,.28)" }}>
            {t.copyright}
          </p>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            {t.legalLinks.map(([l, h]) => (
              <Link key={h} to={h} style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:11, color:"rgba(255,255,255,.28)", textDecoration:"none" }}
                onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,.6)"}
                onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,.28)"}>
                {l}
              </Link>
            ))}
          </div>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:11, color:"rgba(255,255,255,.28)" }}>
            {t.address}
          </p>
        </div>
      </div>
      <style>{`@media (max-width:820px) { .footer-grid { grid-template-columns:1fr !important; } }`}</style>
    </footer>
  );
}

function IconFingerprint(props) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" {...props}>
      <path d="M12 3c-4.4 0-8 3.6-8 8v3" />
      <path d="M12 5.5c-3 0-5.5 2.5-5.5 5.5v2.5" />
      <path d="M12 8c-1.7 0-3 1.3-3 3v3.5c0 2 .8 3.8 2 5" />
      <path d="M12 11c0 4 1 7 3 9" />
      <path d="M15 10.5v2c0 3 1 5.5 2.5 7.5" />
      <path d="M17.5 8.5c.9 1.3 1.5 3 1.5 5v1" />
    </svg>
  );
}

const COOKIE_CONSENT_KEY = "clarity-cookie-consent";

function CookieNotice() {
  const { lang } = useLang();
  const t = content[lang].cookieNotice;
  const [choice, setChoice] = useState(() => {
    try { return window.localStorage.getItem(COOKIE_CONSENT_KEY); }
    catch { return null; }
  });
  const [open, setOpen] = useState(choice === null);

  const choose = (value) => {
    setChoice(value);
    setOpen(false);
    try {
      window.localStorage.setItem(COOKIE_CONSENT_KEY, value);
      if (value === "accepted") window.localStorage.setItem("clarity-map-consent", "1");
    } catch {}
    window.dispatchEvent(new Event("clarity-consent-changed"));
  };

  if (!open) {
    return (
      <button type="button" aria-label={t.reopenLabel} onClick={() => setOpen(true)}
        style={{
          position:"fixed", left:16, bottom:16, zIndex:10000,
          width:44, height:44, borderRadius:"50%", background:"#C0392B", color:C.white,
          border:"none", display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 4px 16px rgba(0,0,0,.25)", cursor:"pointer",
        }}>
        <IconFingerprint />
      </button>
    );
  }

  return (
    <div role="dialog" aria-live="polite" aria-label={t.title} style={{
      position:"fixed", left:16, right:16, bottom:16, zIndex:10000,
      maxWidth:420, margin:"0 auto",
      background:C.white, borderRadius:16, padding:"22px 24px",
      boxShadow:"0 12px 40px rgba(0,0,0,.25)", border:`1px solid ${C.border}`,
    }}>
      <p style={{ margin:"0 0 8px", fontFamily:"'Playfair Display',serif", fontWeight:500, fontSize:16, color:C.dark }}>
        {t.title}
      </p>
      <p style={{ margin:"0 0 16px", fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:13, lineHeight:1.6, color:C.muted }}>
        {t.text}
      </p>
      <div style={{ display:"flex", gap:10, marginBottom:14 }}>
        <button type="button" onClick={() => choose("declined")}
          style={{ flex:1, padding:"11px 16px", borderRadius:24, background:C.white, border:`1.5px solid ${C.border}`, color:C.dark, fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:13, cursor:"pointer" }}>
          {t.decline}
        </button>
        <button type="button" onClick={() => choose("accepted")}
          style={{ flex:1, padding:"11px 16px", borderRadius:24, background:C.sage, border:"none", color:C.white, fontFamily:"'DM Sans',sans-serif", fontWeight:500, fontSize:13, cursor:"pointer" }}>
          {t.accept}
        </button>
      </div>
      <div style={{ display:"flex", gap:16 }}>
        <Link to="/privacy" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage, textDecoration:"underline" }}>{t.privacyLinkLabel}</Link>
        <Link to="/cookies" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:C.sage, textDecoration:"underline" }}>{t.cookieLinkLabel}</Link>
      </div>
    </div>
  );
}

function FAB() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 200);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  if (!show) return null;
  return (
    <a href="tel:+61488988234" style={{
      position:"fixed", bottom:22, right:22, zIndex:9999,
      width:56, height:56, borderRadius:"50%", background:C.sage,
      display:"flex", alignItems:"center", justifyContent:"center",
      boxShadow:"0 6px 22px rgba(61,90,75,.42)",
      textDecoration:"none", animation:"floatY 4s ease-in-out infinite",
    }} className="fab-mob">
      <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:`2px solid rgba(61,90,75,.35)`, animation:"pulseRing 2.5s ease-out infinite" }} />
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="white" strokeWidth="1.5">
        <path d="M3.5 3.5 C3.5 3.5 5.5 1.5 7.5 3.5 L9.5 6.5 C9.5 6.5 10.5 8.5 8.5 10.5 L7.5 11.5 C7.5 11.5 10 14.5 13.5 14 L14.5 13 C16.5 11 18.5 12 18.5 12 L21 14.5 C23 16.5 21 18.5 21 18.5 C21 18.5 19 22 13 20 C7 18 3 14 1 8 C-1 2 3.5 3.5 3.5 3.5Z" transform="scale(.85) translate(1.5,1.5)"/>
      </svg>
      <style>{`@media (min-width:821px) { .fab-mob { display:none !important; } }`}</style>
    </a>
  );
}

function getStoredLang() {
  try {
    const stored = window.localStorage.getItem("clarity-lang");
    return stored === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

export default function App() {
  const [lang, setLangState] = useState(getStoredLang);

  useEffect(() => {
    // Browsers localize native UI (required-field validation messages,
    // screen readers) based on this attribute — it doesn't follow our
    // React lang state automatically.
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (next) => {
    setLangState(next);
    try {
      window.localStorage.setItem("clarity-lang", next);
    } catch {
      // localStorage unavailable — in-memory state still works
    }
  };

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <style>{G}</style>
      <BrowserRouter>
        <ProgressBar />
        <Nav />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/cookies" element={<LegalPage type="cookies" />} />
        </Routes>
        <Footer />
        <FAB />
        <CookieNotice />
      </BrowserRouter>
    </LangContext.Provider>
  );
}

function HomePage() {
  return (
    <main>
      <Hero />
      <Ticker />
      <Help />
      <About />
      <Approach />
      <Testimonials />
      <Telehealth />
      <Quote />
      <CTA />
      <Contact />
    </main>
  );
}

function LegalPage({ type }) {
  const { lang } = useLang();
  const doc = content[lang].legal[type];
  const updated = doc.updated;

  useLayoutEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main style={{ background:C.bg, minHeight:"60vh", padding:"140px clamp(20px,4vw,56px) 100px" }}>
      <div style={{ maxWidth:760, margin:"0 auto" }}>
        <Link to="/" style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:13, color:C.sage, textDecoration:"none" }}>
          &larr; {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
        </Link>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontWeight:400, fontSize:"clamp(30px,4.5vw,44px)", color:C.dark, margin:"24px 0 8px" }}>
          {doc.title}
        </h1>
        <p style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:13, color:C.muted, marginBottom:44 }}>
          {lang === "fr" ? "Dernière mise à jour : " : "Last updated: "}{updated}
        </p>
        {doc.sections.map((s, i) => (
          <div key={i} style={{ marginBottom:32 }}>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:500, fontSize:19, color:C.sage, marginBottom:10 }}>
              {s.heading}
            </h2>
            {s.body.split("\n").map((p, j) => (
              <p key={j} style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:300, fontSize:15, lineHeight:1.85, color:C.dark, marginBottom:10 }}>
                {p}
              </p>
            ))}
          </div>
        ))}
      </div>
    </main>
  );
}
